import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Base styles
  'inline-flex items-center justify-center font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        // Primary solid (existing Button.tsx pattern)
        primary: 'bg-blue-900 text-white hover:bg-blue-800',

        // Secondary (purple)
        secondary: 'bg-purple-600 text-white hover:bg-purple-700',

        // Gradient CTA (emerald-cyan hero buttons from IntroSection)
        gradient:
          'bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.02] active:scale-95',

        // Gradient login (blue-cyan from LoginPage)
        'gradient-blue':
          'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 hover:scale-[1.02]',

        // Gradient signup/submit (emerald-teal) - primary CTA style
        'gradient-emerald':
          'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-[1.02] disabled:hover:scale-100',

        // Gradient rose (rose-pink) - Record page CTA
        'gradient-rose':
          'bg-gradient-to-r from-rose-600 to-pink-500 text-white shadow-lg shadow-rose-500/30 hover:shadow-xl hover:shadow-rose-500/40 hover:scale-[1.02] disabled:hover:scale-100',

        // Outline
        outline:
          'border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white bg-transparent',

        // Ghost (card action buttons)
        ghost: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100',

        // Destructive (delete actions)
        destructive: 'text-red-600 hover:text-red-700 hover:bg-red-50',

        // Success/Submit (indigo solid)
        success: 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md',

        // Dark (slate-900 modal close buttons)
        dark: 'bg-slate-900 text-white hover:bg-slate-800',

        // Amber (templates/warning actions)
        amber: 'bg-amber-600 text-white hover:bg-amber-700',

        // Blue solid (standard action)
        blue: 'bg-blue-600 text-white hover:bg-blue-700',

        // Navigation ghost with colors
        'nav-red': 'bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700',
        'nav-red-active': 'bg-red-100 text-red-700 ring-2 ring-red-300',
        'nav-green': 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700',
        'nav-green-active': 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300',
        'nav-purple': 'bg-purple-50 text-purple-600 hover:bg-purple-100 hover:text-purple-700',
        'nav-purple-active': 'bg-purple-100 text-purple-700 ring-2 ring-purple-300',
        'nav-slate': 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900',
        'nav-slate-active': 'bg-slate-200 text-slate-900',
      },
      size: {
        xs: 'px-2 py-1 text-xs gap-1',
        sm: 'px-3 py-1.5 text-sm gap-1.5',
        md: 'px-4 py-2 text-sm gap-2',
        lg: 'px-6 py-3 text-base gap-2',
        xl: 'px-8 py-4 text-lg gap-3',
        // Icon-only sizes
        'icon-sm': 'p-1.5',
        'icon-md': 'p-2',
        'icon-lg': 'p-2.5',
      },
      shape: {
        default: 'rounded-lg',
        rounded: 'rounded-xl',
        pill: 'rounded-full',
        square: 'rounded-md',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      shape: 'default',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shape, fullWidth, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, shape, fullWidth, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
